import { HashLocationStrategy, LOCATION_INITIALIZED, LocationStrategy } from "@angular/common";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withFetch,
    withInterceptors,
    withInterceptorsFromDi
} from "@angular/common/http";
import type { ApplicationConfig } from "@angular/core";
import {
    APP_INITIALIZER,
    ErrorHandler,
    Injector,
    LOCALE_ID,
    importProvidersFrom,
    isDevMode,
    provideZoneChangeDetection
} from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { provideStorage } from "@angular/fire/storage";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DATE_LOCALE, MatNativeDateModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { Router, provideRouter, withComponentInputBinding, withViewTransitions } from "@angular/router";
import { AkitaNgRouterStoreModule } from "@datorama/akita-ng-router-store";
import { AkitaNgDevtools } from "@datorama/akita-ngdevtools";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxFormGeneratorModule } from "@recursyve/ngx-form-generator";
import { NiceAdvancedFiltersModule, NiceBaseListModule, NiceFilterViewModule } from "@recursyve/nice-data-filter-kit";
import {
    NiceAsyncTypeaheadModule,
    NiceChipListDirectiveModule,
    NiceConfigModule,
    NiceLoadingToastModule,
    NiceModule,
    NiceSweetAlertModule,
    NiceToastModule,
    NiceTransformResponseInterceptor,
    NiceTranslationFormModule,
    RequireForLanguagesValidator
} from "@recursyve/nice-ui-kit.v2";
import * as Sentry from "@sentry/angular";
import { getStorage } from "firebase/storage";
import { provideNgxMask } from "ngx-mask";
import { QuillModule } from "ngx-quill";
import { from, noop } from "rxjs";
import { switchMap } from "rxjs/operators";

import { environment } from "../environments/environment";
import { CategoriesTypeaheadApi } from "./api/categories/apis/categories-typeahead.api";
import { CategorySectionsTypeaheadApi } from "./api/categories/apis/category-sections-typeahead.api";
import { CourseTypeaheadApi } from "./api/courses/apis/course-typeahead.api";
import { DistributorTypeaheadApi } from "./api/distributors/apis/distributor-typeahead.api";
import { EnhancementCapsuleTypeaheadApi } from "./api/enhancement-capsules/apis/enhancement-capsules-typeahead.api";
import { authInterceptor } from "./api/interceptors/auth.interceptor";
import { errorInterceptor } from "./api/interceptors/error.interceptor";
import { languageInterceptor } from "./api/interceptors/language.interceptor";
import { timezoneInterceptor } from "./api/interceptors/timezone.interceptor";
import { LanguagesTypeaheadApi } from "./api/languages/apis/languages-typeahead.api";
import { MemberTypeaheadApi } from "./api/members/apis/member-typeahead.api";
import { OrganisationTypeaheadApi } from "./api/organisations/apis/organisation-typeahead.api";
import { PricingsTypeaheadApi } from "./api/pricings/apis/pricings-typeahead.api";
import { PromotionConditionTypesTypeaheadApi } from "./api/promotion-condition-types/apis/promotion-condition-types-typeahead.api";
import { PromotionTypesTypeaheadApi } from "./api/promotion-types/apis/promotion-types-typeahead.api";
import { SupplierTypeaheadApi } from "./api/suppliers/apis/supplier-typeahead.api";
import { routes } from "./app.routes";
import { CustomAlertComponent } from "./components/custom-alert/custom-alert.component";
import { CourseNameExistsValidator } from "./features/dashboard/courses/modals/create-course/validators/course-name-exists-validator.service";
import { DistributorNameIsUniqueValidator } from "./features/dashboard/distributors/modals/create-distributor-modal/validators/distributor-name-is-unique-validator.service";
import { DistributorSlugIsUniqueValidator } from "./features/dashboard/distributors/modals/create-distributor-modal/validators/distributor-slug-is-unique-validator.service";
import { EnhancementCapsuleNameExistsValidator } from "./features/dashboard/enhancement-capsules/modals/create-enhancement-capsule/validators/enhancement-capsule-name-exists-validator.service";
import { NewsTitleIsUniqueValidator } from "./features/dashboard/news/modals/create-news/validators/news-title-exists-validator.service";
import { OrganisationNameIsUniqueValidator } from "./features/dashboard/organisations/modals/create-organisation-modal/validators/organisation-name-is-unique-validator.service";
import { RequestIdentifierIsUniqueValidator } from "./features/dashboard/requests/modals/create-request-modal/validators/request-identifier-is-unique-validator.service";
import { SupplierNameIsUniqueValidator } from "./features/dashboard/suppliers/modals/create-supplier-modal/validators/supplier-name-is-unique-validator.service";
import { SupplierSlugIsUniqueValidator } from "./features/dashboard/suppliers/modals/create-supplier-modal/validators/supplier-slug-is-unique-validator.service";
import { GlobalErrorHandler } from "./handlers/global-error.handler";
import { FilterGroupsService } from "./providers/filter-groups.service";
import { I18nService } from "./providers/i18n.service";
import { RequireForOneLanguageValidator } from "./validators/required-for-one-language.validator";

export function appInitializerFactory(translate: TranslateService, i18nService: I18nService, injector: Injector) {
    return () => {
        const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
        return from(locationInitialized).pipe(
            switchMap(() => {
                const language = i18nService.getDefaultLang();
                translate.setFallbackLang(language);
                return translate.use(language);
            })
        );
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes, withComponentInputBinding(), withViewTransitions()),
        provideAnimationsAsync(),
        provideHttpClient(
            withFetch(),
            withInterceptorsFromDi(),
            withInterceptors([authInterceptor, errorInterceptor, languageInterceptor, timezoneInterceptor])
        ),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),
        provideStorage(() => getStorage()),
        provideNgxMask(),
        RequireForLanguagesValidator,
        {
            provide: LocationStrategy,
            useClass: HashLocationStrategy
        },
        {
            provide: MatPaginatorIntl,
            useFactory: (i18nService: I18nService) => i18nService.getPaginatorIntl(),
            deps: [I18nService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [TranslateService, I18nService, Injector],
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        {
            provide: Sentry.TraceService,
            deps: [Router]
        },
        {
            provide: LOCALE_ID,
            useValue: "fr"
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: "fr"
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => noop,
            deps: [Sentry.TraceService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NiceTransformResponseInterceptor,
            multi: true
        },
        importProvidersFrom(
            NiceModule,
            NiceConfigModule.forRoot({
                theme: "default",
                scheme: "light"
            }),
            NiceAdvancedFiltersModule.forRoot({
                icons: {
                    add: { svgIcon: "streamline-regular:add" },
                    arrowDown: { svgIcon: "streamline-regular:arrow-down" },
                    close: { svgIcon: "streamline-regular:close-1" },
                    datePicker: { svgIcon: "streamline-regular:calendar" },
                    delete: { svgIcon: "streamline-regular:bin-1" },
                    refresh: { svgIcon: "streamline-regular:refresh" },
                    queryBuilder: { svgIcon: "streamline-regular:filter" }
                },
                filterGroupService: FilterGroupsService
            }),
            NiceChipListDirectiveModule.forRoot({
                closeButtonIcon: {
                    svgIcon: "streamline-solid:remove-circle-1"
                }
            }),
            NiceFilterViewModule.forRoot({
                icons: {
                    search: {
                        svgIcon: "streamline-regular:search"
                    }
                }
            }),
            NiceSweetAlertModule.forRoot({
                customModal: CustomAlertComponent
            }),
            NiceLoadingToastModule.forRoot({
                appearance: "border"
            }),
            NiceToastModule.forRoot({
                appearance: "border"
            }),
            TranslateModule.forRoot({
                loader: provideTranslateHttpLoader({ useHttpBackend: true }),
                fallbackLang: "fr"
            }),
            NiceAsyncTypeaheadModule.register([
                CategoriesTypeaheadApi,
                CategorySectionsTypeaheadApi,
                CourseTypeaheadApi,
                DistributorTypeaheadApi,
                EnhancementCapsuleTypeaheadApi,
                LanguagesTypeaheadApi,
                MemberTypeaheadApi,
                OrganisationTypeaheadApi,
                PricingsTypeaheadApi,
                PromotionConditionTypesTypeaheadApi,
                PromotionTypesTypeaheadApi,
                SupplierTypeaheadApi
            ]),
            NiceBaseListModule.forRoot(),
            NgxFormGeneratorModule.forRoot({
                asyncValidators: [
                    CourseNameExistsValidator,
                    DistributorNameIsUniqueValidator,
                    DistributorSlugIsUniqueValidator,
                    EnhancementCapsuleNameExistsValidator,
                    NewsTitleIsUniqueValidator,
                    OrganisationNameIsUniqueValidator,
                    RequestIdentifierIsUniqueValidator,
                    RequireForOneLanguageValidator,
                    SupplierNameIsUniqueValidator,
                    SupplierSlugIsUniqueValidator
                ]
            }),
            NiceTranslationFormModule.forRoot(),
            QuillModule.forRoot({
                modules: {
                    toolbar: {
                        container: [
                            ["bold", "italic", "underline", { list: "ordered" }, { list: "bullet" }, "clean", "link"]
                        ]
                    }
                }
            }),
            MatNativeDateModule,
            MatButtonModule,
            MatIconModule,
            isDevMode() ? AkitaNgDevtools.forRoot() : [],
            AkitaNgRouterStoreModule
        )
    ]
};
